import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import SiteChrome from './SiteChrome';

const supportEmail = 'affiliates@bettermedaily.co.za';

const Payout = () => {
  const navigate = useNavigate();
  const [availableBalance, setAvailableBalance] = useState(0);
  const [minimumPayoutAmount, setMinimumPayoutAmount] = useState(100);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    bankName: '',
    accountNumber: '',
    accountHolderName: '',
    branchCode: '',
    accountType: 'Savings',
    amount: '',
    agree: false
  });

  useEffect(() => {
    const loadPayoutData = async () => {
      try {
        const dashboard = await api.getAffiliateDashboard();
        setAvailableBalance(Number(dashboard.availableBalance || 0));
        setMinimumPayoutAmount(Number(dashboard.minimumPayoutAmount || 100));
        if (dashboard.bankDetails) {
          setFormData((current) => ({
            ...current,
            bankName: dashboard.bankDetails.bankName || '',
            accountNumber: dashboard.bankDetails.accountNumber || '',
            accountHolderName: dashboard.bankDetails.accountHolderName || '',
            branchCode: dashboard.bankDetails.branchCode || '',
            accountType: dashboard.bankDetails.accountType || 'Savings'
          }));
        }
      } catch (err) {
        setError(err.message || 'Failed to load payout data.');
      } finally {
        setLoading(false);
      }
    };

    loadPayoutData();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await api.requestPayout({
        bankName: formData.bankName,
        accountHolderName: formData.accountHolderName,
        accountNumber: formData.accountNumber,
        branchCode: formData.branchCode,
        accountType: formData.accountType,
        amount: Number(formData.amount)
      });
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Payout request failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SiteChrome>
      <div className="max-w-4xl mx-auto px-5 sm:px-6 py-10 sm:py-12">
        <div className="bg-white rounded-2xl shadow-lg p-5 sm:p-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">Request Payout</h1>

          <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl p-6 text-white mb-8">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold mb-2">Current Available Balance</h2>
                <p className="text-3xl font-bold">R{availableBalance.toFixed(2)}</p>
              </div>
              <div className="text-right">
                <p className="text-sm opacity-90 mb-1">Minimum payout:</p>
                <p className="text-lg font-semibold">R{minimumPayoutAmount.toFixed(2)}</p>
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {!loading && availableBalance < minimumPayoutAmount && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-yellow-800">
                Minimum payout is R{minimumPayoutAmount.toFixed(2)}. Your current available balance is R{availableBalance.toFixed(2)}.
              </p>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-900">
              Payout requests move through three states: requested, processed, or declined. Transfers are sent to South African bank accounts only and may take 3-5 business days after processing. Only referrals with verified email and completed onboarding count toward payout, and earnings are marked as claimed once a payout is processed. Refunds, chargebacks, invalid referrals, or incorrect bank details can delay or reduce a payout. For payout support, email <a href={`mailto:${supportEmail}`} className="font-semibold underline">{supportEmail}</a>.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name *</label>
                <input
                  type="text"
                  name="bankName"
                  value={formData.bankName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                  placeholder="Capitec"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Account Holder Name *</label>
                <input
                  type="text"
                  name="accountHolderName"
                  value={formData.accountHolderName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Account Number *</label>
                <input
                  type="text"
                  name="accountNumber"
                  value={formData.accountNumber}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                  placeholder="1234567890"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Branch Code *</label>
                <input
                  type="text"
                  name="branchCode"
                  value={formData.branchCode}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                  placeholder="470010"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Account Type *</label>
                <input
                  type="text"
                  name="accountType"
                  value={formData.accountType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                  placeholder="Savings"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payout Amount *</label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  required
                  min={minimumPayoutAmount}
                  max={availableBalance}
                  step="0.01"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                  placeholder={minimumPayoutAmount.toFixed(2)}
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="agree"
                checked={formData.agree}
                onChange={handleChange}
                required
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <label className="ml-2 text-sm text-gray-600">
                I confirm that all banking information provided is accurate.
              </label>
            </div>

            <button
              type="submit"
              disabled={loading || submitting || Number(formData.amount || 0) < minimumPayoutAmount || Number(formData.amount || 0) > availableBalance}
              className="w-full bg-primary-600 text-white py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {submitting ? 'Submitting...' : 'Request Payout'}
            </button>
          </form>
        </div>
      </div>
    </SiteChrome>
  );
};

export default Payout;
